import React from "react";
import { ModalWrapper } from "../../../components/ui";
import { Button } from "../../../components/ui";
import { UI_COPY } from "../../../config";
import { useUiText } from "../../../hooks/useUiText";

/**
 * Delete Shift Confirmation Modal
 * Displays a warning message and confirmation buttons for deleting a shift
 * 
 * @param {Object} props - Component props
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {Function} props.onClose - Function to close the modal
 * @param {Function} props.onConfirm - Function to confirm deletion
 * @param {Object} props.shiftInfo - Optional shift information to display
 * @param {string} props.shiftInfo.date - Date of the shift
 * @param {string} props.shiftInfo.jobNum - Job number
 * @param {string} props.shiftInfo.startTime - Start time
 * @param {string} props.shiftInfo.finishTime - Finish time
 * @returns {JSX.Element}
 */
const DeleteShiftModal = ({ isOpen, onClose, onConfirm, shiftInfo }) => {
  const { t } = useUiText();
  /**
   * Confirm deletion then close the modal.
   *
   * @returns {void}
   */
  const handleConfirm = () => {
    onConfirm();
    onClose();
  };

  return (
    <ModalWrapper isOpen={isOpen} onClose={onClose}>
      <div className="text-center p-4">
        <div className="mb-4">
          {/* Warning Icon */}
          <div className="text-red-600 text-6xl mb-4">⚠️</div>
          
          {/* Warning Message */}
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            {t(UI_COPY.timesheet.delete.title)}
          </h2>
          
          <p className="text-gray-600 mb-2 font-semibold">
            {t(UI_COPY.timesheet.delete.cannotUndo)}
          </p>
          
          <p className="text-gray-600 mb-4">
            {t(UI_COPY.timesheet.delete.clockInAgain)}
          </p>

          {/* Optional shift info display */}
          {shiftInfo && (
            <div className="bg-gray-100 rounded-md p-3 mb-4 text-left">
              <p className="text-sm text-gray-700">
                <span className="font-semibold">{t(UI_COPY.timesheet.delete.date)}</span>{" "}
                {shiftInfo.date}
              </p>
              {shiftInfo.jobNum && (
                <p className="text-sm text-gray-700">
                  <span className="font-semibold">{t(UI_COPY.timesheet.delete.job)}</span>{" "}
                  {shiftInfo.jobNum}
                </p>
              )}
              {shiftInfo.startTime && shiftInfo.finishTime && (
                <p className="text-sm text-gray-700">
                  <span className="font-semibold">{t(UI_COPY.timesheet.delete.time)}</span>{" "}
                  {shiftInfo.startTime} - {shiftInfo.finishTime}
                </p>
              )}
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex gap-4 justify-center">
          <Button
            text={t(UI_COPY.nav.cancel)}
            variant="secondary"
            className="flex-1"
            onClick={onClose}
          />
          <Button
            text={t(UI_COPY.timesheet.delete.confirm)}
            variant="warning"
            className="flex-1"
            onClick={handleConfirm}
          />
        </div>
      </div>
    </ModalWrapper>
  );
};

export default DeleteShiftModal;
